import { useCallback, useEffect, useRef, useState } from 'react';
import { supabase } from '../lib/supabase';
import { loadProducts, loadTemplate } from '../lib/data';
import { computeProductSummary } from '../shared/criticalPath';
import { buildTemplateForType } from '../shared/terceirizacao';
import type { Product, ProductSummary, StageTemplate } from '../shared/types';

interface OrgData {
  loading: boolean;
  error: string | null;
  template: StageTemplate[];
  products: Product[];
  summaries: ProductSummary[];
  refresh: () => void;
}

/**
 * Carrega template + produtos da org, calcula os resumos e mantém tudo ao vivo
 * via Supabase Realtime (assina product_stages e products).
 */
export function useOrgData(orgId: string | null): OrgData {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [template, setTemplate] = useState<StageTemplate[]>([]);
  const [products, setProducts] = useState<Product[]>([]);
  const [summaries, setSummaries] = useState<ProductSummary[]>([]);
  const debounce = useRef<ReturnType<typeof setTimeout> | null>(null);

  const load = useCallback(async () => {
    if (!orgId) return;
    try {
      setError(null);
      const [tpl, prods] = await Promise.all([loadTemplate(orgId), loadProducts(orgId)]);
      setTemplate(tpl);
      setProducts(prods);
      const now = new Date();
      setSummaries(
        prods.map((p) =>
          computeProductSummary(p, buildTemplateForType(tpl, p.projectType ?? 'interno'), now),
        ),
      );
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Falha ao carregar dados.');
    } finally {
      setLoading(false);
    }
  }, [orgId]);

  useEffect(() => {
    setLoading(true);
    load();
  }, [load]);

  // Realtime
  useEffect(() => {
    if (!orgId) return;
    const scheduleReload = () => {
      if (debounce.current) clearTimeout(debounce.current);
      debounce.current = setTimeout(load, 250);
    };
    const channel = supabase
      .channel(`org-${orgId}`)
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'product_stages', filter: `org_id=eq.${orgId}` },
        scheduleReload,
      )
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'products', filter: `org_id=eq.${orgId}` },
        scheduleReload,
      )
      .subscribe();
    return () => {
      supabase.removeChannel(channel);
    };
  }, [orgId, load]);

  return { loading, error, template, products, summaries, refresh: load };
}
